import { backgroundCheckStatusUpdate } from '@/backend/features/background-check';
import { getBackgroundTaskStateStorage } from '@juvare/contact-management/backend/features/background-task-state';
import { isProdEnvironment } from '@juvare/contact-management/backend/utils';
import { BackgroundTaskDef } from '@juvare/platform-app/App';
import { BackgroundCheckResponse } from '@juvare/platform-app/Integration';
import { StorageBackendApi } from '@juvare/platform-app/Storage';
import { AppState } from '@juvare/platform-sdk-react/types';
import { BackgroundCheckData, BackgroundCheckHistoryColumns, BackgroundCheckHistoryData } from '../entities';
import { ParsedBackgroundCheck, cleanResults, parseBackgroundCheck } from '../platform-functions/utils';
import { BackgroundCheckStatus, BackgroundCheckType } from '../types';

const secondsInADay = 86400;
const taskName = 'backgroundCheckStatusUpdate';

export const backgroundCheckStatusUpdateTask: BackgroundTaskDef<AppState> = {
    callback: (context) => {
        const backgroundTaskStateStorage = getBackgroundTaskStateStorage(context);
        const backgroundTaskStateResponse = backgroundTaskStateStorage.list({
            filter: [{ column: 'taskName', op: '=', value: taskName }],
            limit: 1,
        });
        const backgroundTaskState = backgroundTaskStateResponse.total > 0 ? backgroundTaskStateResponse.rows[0] : null;
        // default bounce timestamp - 1 day
        const lastRunTimestampInSeconds = backgroundTaskState?.columns.lastRunTimestamp ?? Math.floor(Date.now() / 1000 - secondsInADay);
        const runTimestampInSeconds = Math.floor(Date.now() / 1000);

        if (isProdEnvironment(context.environment) && lastRunTimestampInSeconds > runTimestampInSeconds - secondsInADay) {
            return;
        }

        backgroundCheckStatusUpdate(context);

        backgroundTaskStateStorage.post({
            action: 'create-or-update',
            uniqueColumn: 'taskName',
            data: {
                taskName: taskName,
                lastRunTimestamp: runTimestampInSeconds,
            },
        });
    },
    delaySeconds: 300,
};

interface CreateHistoryParams {
    historyStorage: StorageBackendApi<BackgroundCheckHistoryData, BackgroundCheckHistoryColumns>;
    type: BackgroundCheckType;
    accountId: string;
    accountData?: BackgroundCheckData;
    response?: BackgroundCheckResponse;
    nextStatus: BackgroundCheckStatus;
    message?: string;
    stopCheckingForResultsHistoryId?: string;
    responseXml?: string;
    internalStatusChange?: boolean;
}

export function createHistory({
    historyStorage,
    type,
    accountId,
    accountData,
    response,
    nextStatus,
    message,
    responseXml,
    stopCheckingForResultsHistoryId,
    internalStatusChange,
}: CreateHistoryParams) {
    if (stopCheckingForResultsHistoryId) {
        historyStorage.post({
            action: 'partial-update',
            id: stopCheckingForResultsHistoryId,
            data: {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                checkResponseByRequestId: null as any, // should work, need to update platform type definitions
            },
        });
    }

    let parsedXml: ParsedBackgroundCheck | undefined = undefined;

    if (responseXml) {
        try {
            parsedXml = parseBackgroundCheck(responseXml);
        } catch (e) {
            console.warn('There was an error when parsing background check');
        }
    }

    historyStorage.post({
        action: 'create',
        data: {
            accountId,
            type,
            prevStatus: accountData?.backgroundCheck ? accountData.backgroundCheck[type]?.status : undefined,
            nextStatus,
            requestId: response?.executionId,
            checkResponseByRequestId: response?.executionId,
            message: message ?? response?.message,
            responseXml: responseXml ? cleanResults(responseXml) : undefined,
            parsedXml: parsedXml,
            internalStatusChange: internalStatusChange,
        },
    });
}
